"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import { useDropzone } from "react-dropzone"
import { Camera, ScanSearch, Stethoscope, Loader2, AlertTriangle, Lock, Shield, Sun, ScanLine, Maximize, Clock, Images } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { SamplePhotos } from "./sample-photos"
import { DesktopScanHandoff } from "./desktop-scan-handoff"
import { UPLOAD_ACCEPT, UPLOAD_MAX_BYTES_HOMEPAGE } from "@/lib/upload-constants"
import { trackEvent, analysisFunnelEnvelope } from "@/lib/analytics"
import { useAdDockInset } from "@/hooks/use-bottom-dock-inset"
import { ensureAnalysisFunnelId } from "@/lib/analysis-funnel-id"
import {
  trackUploadPrecheckRejected,
  UPLOAD_REJECTION_COPY,
  uploadReasonFromDropzoneCode,
  type UploadPrecheckReason,
} from "@/lib/upload-rejection"
import type { AnalysisMode as SharedAnalysisMode } from "@/lib/types/diagnosis"

type AnalysisMode = Exclude<SharedAnalysisMode, 'weed'>

// Photo-quality "capture rules" shown under the sample photos, matched to the
// task: grass identification wants the whole blade in frame and a single
// species; problem diagnosis wants the affected spot up close. Same 3 icons,
// task-appropriate copy (specificity — the rule names the actual shot to take).
const CAPTURE_RULES = {
  identify: [
    { icon: Sun, label: "Bright daylight" },
    { icon: ScanLine, label: "Fill the frame with blades" },
    { icon: Maximize, label: "One grass type only" },
  ],
  diagnose: [
    { icon: Sun, label: "Bright daylight" },
    { icon: ScanLine, label: "Close to the problem" },
    // The good/bad boundary is the single most diagnostic feature in a turf
    // photo — name it rather than the vaguer "show the affected spot".
    { icon: Maximize, label: "The edge where good meets bad" },
  ],
} as const

export type UploadSource = "file" | "drop" | "camera"

/**
 * Which control the user activated to start an upload. `grass_id_upload_started`
 * fires from inside handleImageUpload, i.e. only once a File already exists, so
 * it cannot see anyone who opened the OS picker and backed out. These locations
 * mark the moment of intent instead; subtracting upload_started from them gives
 * the picker-abandon rate.
 */
export type UploadCtaLocation =
  /** The big picker button (primary on desktop, "Choose from library" on touch). */
  | "library_button"
  /** In-card device-camera button, coarse pointers only. */
  | "camera_button"
  /** Diagnose-only sticky bottom capture bar, coarse pointers only. */
  | "camera_sticky_bar"
  /** A file dragged onto the card. No picker, so no abandon window. */
  | "dropzone_drop"
  /** Enter/Space on the focused dropzone root, which opens the picker too. */
  | "dropzone_keyboard"

// Coarse capture path per control, using the same vocabulary as the `source`
// stamped on grass_id_upload_started so the two events line up 1:1 per path.
const CTA_CAPTURE_PATH: Record<UploadCtaLocation, UploadSource> = {
  library_button: "file",
  camera_button: "camera",
  camera_sticky_bar: "camera",
  dropzone_drop: "drop",
  dropzone_keyboard: "file",
}

interface UploadCardProps {
  onUpload: (file: File, source?: UploadSource) => void
  isUploading: boolean
  uploadError: string
  mode?: AnalysisMode
  className?: string
}

export function UploadCard({
  onUpload,
  isUploading,
  uploadError,
  mode = "identify",
  className,
}: UploadCardProps) {
  const pathname = usePathname()
  // The Mediavine anchor ad is fixed to the viewport bottom and can sit on top
  // of the touch capture bar below, which is the primary control on a phone.
  const adDockInset = useAdDockInset()

  // Counts activations within this mount so a re-open after a cancelled picker
  // is distinguishable from a first attempt. Reset naturally: the card unmounts
  // once an upload succeeds and the flow advances to the ZIP step.
  const ctaClickCountRef = useRef(0)

  // Intent marker for the upload funnel. Strictly user-initiated — never call
  // this from an effect, or it becomes another mount-time event polluting
  // sessions that were never foregrounded.
  const fireUploadCtaClicked = useCallback(
    (ctaLocation: UploadCtaLocation) => {
      // Warm the next step's lazy chunk at the moment of intent. The picker and
      // the compression pass buy a second or two; without this the ZIP step
      // pays a cold chunk fetch and shows "Loading the location step...".
      import("@/components/home/zip-code-step").catch(() => {})
      const capturePath = CTA_CAPTURE_PATH[ctaLocation]
      ctaClickCountRef.current += 1
      trackEvent("grass_id_upload_cta_clicked", {
        ...analysisFunnelEnvelope({
          funnelId: ensureAnalysisFunnelId(),
          mode,
          source: capturePath,
          imageRoles: ["primary"],
          // A drop hands over the File immediately; every other path parks the
          // user in the OS picker, which is where the missing population sits.
          actualDestination: capturePath === "drop" ? "image_upload" : "file_picker",
        }),
        cta_location: ctaLocation,
        capture_path: capturePath,
        // The homepage renders this card in BOTH modes (?mode=diagnose keeps
        // the user on "/"), so `mode` alone cannot separate homepage from
        // /diagnose. The path can.
        page_path: pathname,
        cta_click_index: ctaClickCountRef.current,
      })
    },
    [mode, pathname],
  )

  const { getRootProps, getInputProps, isDragActive, open, fileRejections } =
    useDropzone({
      onDrop: (accepted, _rejections, event) => {
        // Distinguish a real drag-drop from the click-to-pick path (open()
        // routes through onDrop too, with a change event on the hidden input).
        // DropEvent is a union that includes FileSystemFileHandle[], so probe
        // for a string `type` instead of assuming an Event.
        const eventType =
          !Array.isArray(event) && typeof (event as { type?: unknown })?.type === "string"
            ? (event as { type: string }).type
            : ""
        const isDrop = eventType === "drop"
        // Drops have no picker step, so their intent marker only exists here.
        // Gate on isDrop: the picker paths already fired on their own click and
        // would otherwise double-count when open() lands back in onDrop.
        if (isDrop) fireUploadCtaClicked("dropzone_drop")
        if (accepted[0]) onUpload(accepted[0], isDrop ? "drop" : "file")
      },
      onDropRejected: (rejections, event) => {
        const eventType =
          event && typeof (event as { type?: unknown }).type === "string"
            ? (event as { type: string }).type
            : ""
        const rejection = rejections[0]
        trackUploadPrecheckRejected({
          reason: uploadReasonFromDropzoneCode(rejection?.errors[0]?.code),
          capturePath: eventType === "drop" ? "drop" : "file",
          mode,
          funnelId: ensureAnalysisFunnelId(),
          fileSizeBytes: rejection?.file?.size,
        })
      },
      accept: UPLOAD_ACCEPT,
      maxSize: UPLOAD_MAX_BYTES_HOMEPAGE,
      multiple: false,
      disabled: isUploading,
      noClick: true,
    })

  const dropzoneError = fileRejections[0]
    ? UPLOAD_REJECTION_COPY[uploadReasonFromDropzoneCode(fileRejections[0].errors[0]?.code)]
    : ""

  // Device-camera capture path. The hidden input below opens the rear camera
  // on touch devices; its onChange routes the captured file through the SAME
  // validation gate the dropzone enforces (UPLOAD_ACCEPT types +
  // UPLOAD_MAX_BYTES_HOMEPAGE cap) and the SAME onUpload analyze handler, so
  // there is no parallel/divergent analyze path. Rejections surface through
  // the same errorMessage channel as the dropzone.
  const cameraInputRef = useRef<HTMLInputElement | null>(null)
  const [captureError, setCaptureError] = useState("")

  const ACCEPTED_MIME_TYPES = Object.keys(UPLOAD_ACCEPT)

  const onCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset so re-taking the same photo re-fires onChange.
    e.target.value = ""
    if (!file) return

    const rejectCapture = (reason: UploadPrecheckReason) => {
      setCaptureError(UPLOAD_REJECTION_COPY[reason])
      trackUploadPrecheckRejected({
        reason,
        capturePath: "camera",
        mode,
        funnelId: ensureAnalysisFunnelId(),
        fileSizeBytes: file.size,
      })
    }
    if (file.type && !ACCEPTED_MIME_TYPES.includes(file.type)) {
      rejectCapture("invalid_file_type")
      return
    }
    if (file.size > UPLOAD_MAX_BYTES_HOMEPAGE) {
      rejectCapture("file_too_large")
      return
    }

    setCaptureError("")
    onUpload(file, "camera")
  }

  const errorMessage = uploadError || dropzoneError || captureError

  // Header's "Identify my grass" CTA dispatches `highlightUploadCta` after
  // scrolling the card into view, so the user can see where the click landed.
  // Done via classList + forced reflow (the canonical CSS-animation restart
  // trick) so re-clicks during the active window cleanly replay the pulse —
  // React batching makes a useState toggle unreliable for this.
  const beaconRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    let off: ReturnType<typeof setTimeout> | undefined
    const onHighlight = () => {
      const el = beaconRef.current
      if (!el) return
      el.classList.remove("cta-beacon")
      // Forced reflow — read offsetWidth so the browser flushes the class
      // removal before we add it back, restarting the CSS animation.
      void el.offsetWidth
      el.classList.add("cta-beacon")
      if (off) clearTimeout(off)
      off = setTimeout(() => {
        beaconRef.current?.classList.remove("cta-beacon")
      }, 2400)
    }
    window.addEventListener("highlightUploadCta", onHighlight)
    return () => {
      window.removeEventListener("highlightUploadCta", onHighlight)
      if (off) clearTimeout(off)
    }
  }, [])

  // Diagnose-only sticky capture bar: once the in-card CTA scrolls out of view
  // the action follows the thumb instead of being stranded above the samples.
  // Observed rather than always-on so the bar never duplicates a CTA the user
  // can already see. Starts false, so SSR and first paint agree (no flash).
  const ctaAnchorRef = useRef<HTMLDivElement | null>(null)
  const [ctaOutOfView, setCtaOutOfView] = useState(false)
  useEffect(() => {
    const el = ctaAnchorRef.current
    if (!el || typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(
      ([entry]) => setCtaOutOfView(!entry.isIntersecting),
      { threshold: 0 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const errorBlock = errorMessage ? (
    <div
      role="alert"
      className="mt-4 max-w-md mx-auto rounded-xl border border-red-200/80 bg-linear-to-br from-red-50 to-rose-50/70 px-4 py-3 text-left shadow-[0_1px_2px_rgba(220,38,38,0.06),0_4px_12px_-4px_rgba(220,38,38,0.10)]"
    >
      <div className="flex items-start gap-2.5">
        <div className="flex-shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full border border-red-200 bg-white text-red-600 shadow-xs">
          <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0 text-[13px] leading-snug">
          <p className="font-semibold text-red-900">Upload couldn&apos;t finish</p>
          <p className="text-red-800/90 mt-0.5">{errorMessage}</p>
        </div>
      </div>
    </div>
  ) : null

  // The device-camera input is shared by the in-card button and the sticky bar.
  const cameraInput = (
    <input
      ref={cameraInputRef}
      type="file"
      accept="image/*"
      capture="environment"
      onChange={onCameraCapture}
      className="hidden"
      aria-hidden="true"
      tabIndex={-1}
      data-ph-no-capture="true"
    />
  )

  // Every control that opens the OS camera or picker routes through one of
  // these, so the intent event and the actual open can never drift apart.
  const openCamera = (ctaLocation: UploadCtaLocation) => {
    if (isUploading) return
    fireUploadCtaClicked(ctaLocation)
    cameraInputRef.current?.click()
  }

  const openLibrary = () => {
    if (isUploading) return
    fireUploadCtaClicked("library_button")
    open()
  }

  // react-dropzone leaves the card root focusable and opens the picker on
  // Enter/Space even with noClick. Mirror its own target check so keystrokes
  // from children (the ZIP field, buttons) cannot bubble up and inflate counts.
  const onRootKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isUploading) return
    if (event.target !== event.currentTarget) return
    if (event.key !== "Enter" && event.key !== " ") return
    fireUploadCtaClicked("dropzone_keyboard")
  }

  // ── Diagnose layout ──────────────────────────────────────────────────────
  // Single centered decision column inside one dashed drop target: action
  // first, trust line, then a divider with the reference material below it, so
  // nothing competes with the CTA. On touch devices camera is primary and the
  // library picker is demoted, because the user is usually standing on the lawn.
  if (mode === "diagnose") {
    return (
      <>
        <div
          {...getRootProps({
            onKeyDown: onRootKeyDown,
            className: cn(
              "rounded-3xl border-2 border-dashed border-green-300 bg-white/90 px-4 py-6 text-center transition-all duration-200 sm:px-10 sm:py-9",
              isDragActive &&
                "scale-[1.01] border-green-500 bg-green-50/60 shadow-xl shadow-green-600/10",
              className,
            ),
          })}
        >
          <input
            {...getInputProps({
              "aria-label": "Upload lawn photo",
              className: "ph-no-capture",
              "data-ph-no-capture": "true",
            })}
          />

          {isDragActive && (
            <div className="mb-4 flex flex-col items-center gap-1.5 animate-scale-in">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-700">
                <ScanSearch className="h-5 w-5" />
              </span>
              <p className="text-base font-semibold text-green-700">
                Drop your photo to diagnose it
              </p>
            </div>
          )}

          <div ref={ctaAnchorRef} className="mx-auto max-w-md">
            <div ref={beaconRef} className="rounded-full">
              {cameraInput}
              {/* Touch/tablet only: `capture` is ignored on desktop, so a camera
                  button there would just duplicate the file picker. Pointer
                  media queries keep this SSR-safe (no JS device detection). */}
              <Button
                type="button"
                size="lg"
                onClick={() => openCamera("camera_button")}
                disabled={isUploading}
                className={cn(
                  "mb-2.5 hidden w-full rounded-full bg-linear-to-r from-green-600 to-emerald-600 py-6 text-lg font-bold text-white [@media(pointer:coarse)]:inline-flex",
                  "shadow-lg shadow-green-600/30 hover:from-green-700 hover:to-emerald-700",
                  "transition-transform duration-200 active:scale-[0.98]",
                )}
              >
                <Camera className="mr-2 h-6 w-6" />
                Take a photo
              </Button>

              {/* Primary on desktop, demoted to the secondary "library" action
                  once a coarse pointer promotes the camera button above. */}
              <Button
                size="lg"
                onClick={openLibrary}
                disabled={isUploading}
                className={cn(
                  "w-full rounded-full bg-linear-to-r from-green-600 to-emerald-600 py-6 text-xl font-bold text-white",
                  "shadow-lg shadow-green-600/30 hover:from-green-700 hover:to-emerald-700",
                  "transition-[transform,box-shadow,background] duration-300 hover:scale-[1.02] active:scale-[0.98]",
                  "[@media(pointer:coarse)]:border [@media(pointer:coarse)]:border-gray-200 [@media(pointer:coarse)]:bg-white [@media(pointer:coarse)]:bg-none",
                  "[@media(pointer:coarse)]:py-5 [@media(pointer:coarse)]:text-base [@media(pointer:coarse)]:font-semibold [@media(pointer:coarse)]:text-gray-700",
                  "[@media(pointer:coarse)]:shadow-none [@media(pointer:coarse)]:hover:scale-100",
                )}
              >
                {isUploading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <>
                    <Stethoscope className="mr-2 h-6 w-6 [@media(pointer:coarse)]:hidden" />
                    <Images className="mr-2 hidden h-5 w-5 [@media(pointer:coarse)]:inline" />
                    <span className="[@media(pointer:coarse)]:hidden">Diagnose my lawn</span>
                    <span className="hidden [@media(pointer:coarse)]:inline">Choose from library</span>
                  </>
                )}
              </Button>
            </div>

            <p className="mt-2.5 hidden text-sm text-gray-500 [@media(pointer:fine)]:block">
              or drag and drop a photo onto this card
            </p>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-gray-500 sm:text-sm">
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
              Free, no account
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
              Usually under 25 seconds
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
              Deleted after 30 days
            </span>
          </div>

          <DesktopScanHandoff mode="diagnose" pagePath={pathname} />

          {errorBlock}

          {/* Reference material below the divider: it informs the shot, it is
              not part of the decision to start. */}
          <div className="mt-7 grid gap-5 border-t border-gray-100 pt-6 text-left md:grid-cols-2 md:gap-8">
            <div>
              <p className="mb-2.5 text-xs font-bold uppercase tracking-wider text-gray-500">
                What a good diagnosis photo shows
              </p>
              <SamplePhotos
                mode="diagnose"
                className="m-0 max-w-none gap-3 sm:m-0 sm:max-w-none sm:gap-3 md:gap-3"
              />
            </div>
            <div className="rounded-2xl border border-green-100 bg-green-50 px-4 py-4">
              <p className="text-[13px] font-bold text-green-900">A good shot has three things</p>
              <div className="mt-2.5 flex flex-col gap-2 text-[13px] text-green-800">
                {CAPTURE_RULES.diagnose.map(({ icon: RuleIcon, label }) => (
                  <span key={label} className="inline-flex items-center gap-2">
                    <RuleIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
                    {label}
                  </span>
                ))}
              </div>
              {/* Static preview so first-timers know what comes back before
                  they commit a photo. Marketing mock, no live data — keep it
                  free of results-section/analysis-card ids, which the chat
                  widget watches to unmount itself. */}
              <div className="mt-3.5 border-t border-green-200/70 pt-3">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-green-700">
                  Example result
                </p>
                <p className="mt-1 text-[15px] font-bold leading-snug text-gray-900">Brown Patch</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                    Moderate
                  </span>
                  <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-semibold text-white">
                    Treat Soon
                  </span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-600 ring-1 ring-inset ring-black/[0.08]">
                    86% match
                  </span>
                </div>
                <p className="mt-2 text-[13px] leading-snug text-gray-700">
                  <span className="font-semibold text-gray-900">First step:</span> hold off on
                  nitrogen; water pre-dawn, not evenings
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky capture bar — touch only, and only once the in-card CTA is
            gone. env(safe-area-inset-bottom) goes inline: dynamic/safe-area
            values don't survive the Tailwind precompile step. */}
        {ctaOutOfView && (
          <div
            className="fixed inset-x-0 bottom-0 z-40 hidden bg-linear-to-t from-white from-70% to-transparent px-4 pt-6 [@media(pointer:coarse)]:block"
            style={{ paddingBottom: `calc(1rem + env(safe-area-inset-bottom) + ${adDockInset}px)` }}
          >
            <Button
              type="button"
              size="lg"
              onClick={() => openCamera("camera_sticky_bar")}
              disabled={isUploading}
              className="w-full rounded-full bg-linear-to-r from-green-600 to-emerald-600 py-6 text-lg font-bold text-white shadow-lg shadow-green-600/30 hover:from-green-700 hover:to-emerald-700 active:scale-[0.98]"
            >
              <Camera className="mr-2 h-5 w-5" />
              Take a photo
            </Button>
          </div>
        )}
      </>
    )
  }

  return (
    <div
      {...getRootProps({
        onKeyDown: onRootKeyDown,
        className: cn(
          "text-center rounded-2xl transition-all duration-200",
          // Drop target lights up and lifts — a clear, rewarding response to the
          // drag rather than a faint ring. Specificity rule: name the payoff.
          isDragActive &&
            "scale-[1.01] ring-2 ring-green-500 ring-offset-2 bg-green-50/50 shadow-xl shadow-green-600/10",
          className,
        ),
      })}
    >
      <input
        {...getInputProps({
          "aria-label": "Upload grass photo",
          className: "ph-no-capture",
          "data-ph-no-capture": "true",
        })}
      />

      {isDragActive && (
        <div className="mb-4 flex flex-col items-center gap-1.5 animate-scale-in">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-700">
            <ScanSearch className="h-5 w-5" />
          </span>
          <p className="text-base font-semibold text-green-700">
            Drop your photo to identify it
          </p>
        </div>
      )}

      <div className="rounded-2xl">
      <SamplePhotos mode={mode} />

      {/* Capture rules — set photo-quality expectations before the upload click
          so the first shot lands (fewer retries / abandons). Complements the
          sample-photo captions rather than repeating them. */}
      <div className="mx-auto mb-3 mt-1 flex max-w-md flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs font-medium text-gray-500 animate-fade-in sm:mb-5">
        {CAPTURE_RULES[mode].map(({ icon: RuleIcon, label }) => (
          <span key={label} className="inline-flex items-center gap-1.5">
            <RuleIcon className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
            {label}
          </span>
        ))}
      </div>

      {/* Upload button */}
      <div className="max-w-md mx-auto">
        <div ref={beaconRef} className="rounded-lg">
        <Button
          size="lg"
          onClick={openLibrary}
          disabled={isUploading}
          className={cn(
            "w-full bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-lg text-xl py-6",
            "shadow-lg shadow-green-600/25 hover:shadow-xl hover:shadow-green-600/30",
            "transition-[transform,box-shadow,background] duration-300",
            "hover:scale-[1.02]",
            "active:scale-[0.98]",
          )}
        >
          {isUploading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <>
              <ScanSearch className="w-6 h-6 mr-2" />
              Identify my grass
            </>
          )}
        </Button>
        </div>

        <p className="mt-2 text-xs font-medium text-gray-500">
          Choose a photo from your library
          <span className="hidden [@media(pointer:coarse)]:inline"> or use your camera</span>
        </p>

        {/* Secondary capture affordance — touch/tablet only. The `capture`
            attribute is ignored on desktop, so a "Take a photo" button there
            would just duplicate the upload click; we hide it on fine pointers
            via a pure CSS media query (SSR-safe, no JS device detection / no
            hydration mismatch) and reveal it only on coarse pointers. */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onCameraCapture}
          className="hidden"
          aria-hidden="true"
          tabIndex={-1}
          data-ph-no-capture="true"
        />
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={() => openCamera("camera_button")}
          disabled={isUploading}
          className="mt-3 hidden w-full rounded-lg border-green-600/30 py-5 text-base font-semibold text-green-700 hover:bg-green-50 hover:text-green-800 [@media(pointer:coarse)]:inline-flex"
        >
          <Camera className="mr-2 h-5 w-5" />
          Take a photo
        </Button>

        <div
          className="mt-3 mx-auto grid w-fit max-w-full items-start gap-x-1.5 gap-y-2 text-xs text-gray-500"
          style={{ gridTemplateColumns: 'auto 1fr' }}
        >
          <Lock className="mt-0.5 h-3 w-3" aria-hidden="true" />
          <span>Free · No credit card · Usually under 20 seconds</span>
          <Shield className="mt-0.5 h-3 w-3" aria-hidden="true" />
          <span>Photos used only for grass ID. Deleted after 30 days.</span>
        </div>

        <DesktopScanHandoff mode="identify" pagePath={pathname} />
      </div>
      </div>

      {/* Upload error — single source of truth (toast is suppressed for upload
          errors since this is contextually anchored to the upload action). */}
      {errorMessage && (
        <div
          role="alert"
          className="mt-4 max-w-md mx-auto rounded-xl border border-red-200/80 bg-linear-to-br from-red-50 to-rose-50/70 px-4 py-3 text-left shadow-[0_1px_2px_rgba(220,38,38,0.06),0_4px_12px_-4px_rgba(220,38,38,0.10)]"
        >
          <div className="flex items-start gap-2.5">
            <div className="flex-shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full border border-red-200 bg-white text-red-600 shadow-xs">
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.25} />
            </div>
            <div className="flex-1 min-w-0 text-[13px] leading-snug">
              <p className="font-semibold text-red-900">Upload couldn&apos;t finish</p>
              <p className="text-red-800/90 mt-0.5">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
